'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { onPostHogReady } from '@/instrumentation-client'
import { useAuthContext } from '@/hooks/use-auth-context'
import { useOptionalConsent } from '@/components/analytics/consent-provider'
import { allowsAnalytics, allowsMarketing } from '@/lib/analytics/consent'
import { captureCurrentCampaignContext, trackConnect } from '@/lib/analytics/track'

/** Observe the shared server-rendered landing/source pages without changing links. */
export function ConnectFunnelObserver() {
  const pathname = usePathname()
  const consent = useOptionalConsent()
  const analytics = allowsAnalytics(consent)
  const marketing = allowsMarketing(consent)
  const { user, org } = useAuthContext()
  const seenAds = useRef<string | null>(null)
  const seenProduct = useRef<string | null>(null)
  useEffect(() => {
    captureCurrentCampaignContext()
    const match = /^\/connect\/data\/([^/]+)$/.exec(pathname)
    if (!match) return
    const slug = decodeURIComponent(match[1])
    const props = { source_slug: slug, user_id: user?.id, organization_id: org?.id,
      content_type: 'product', content_ids: [slug], items: [{ item_id: slug }] }
    if (marketing && seenAds.current !== pathname) {
      seenAds.current = pathname
      trackConnect('connect_source_viewed', props,
        { posthogOnly: false, marketingOnly: true, metaStandardEvent: 'ViewContent', googleEvent: 'view_item' })
    }
    if (analytics && seenProduct.current !== pathname) {
      return onPostHogReady(() => {
        seenProduct.current = pathname
        trackConnect('connect_source_viewed', props)
      })
    }
  }, [pathname, analytics, marketing, user?.id, org?.id])
  useEffect(() => {
    if (!pathname.startsWith('/connect')) return
    const clicked = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest('a') : null
      if (!target) return
      const label = target.textContent?.trim().replace(/\s+/g, ' ').toLowerCase()
      const action = label === 'start free' ? 'start_free' : label === 'sign in' ? 'sign_in' : null
      if (action) trackConnect('connect_cta_clicked', {
        action, user_id: user?.id, organization_id: org?.id,
        source_slug: /^\/connect\/data\/([^/]+)$/.exec(pathname)?.[1],
      })
    }
    document.addEventListener('click', clicked, true)
    return () => document.removeEventListener('click', clicked, true)
  }, [pathname, user?.id, org?.id])
  return null
}
